import { useEffect, useRef, useState } from "react";
import { Images, PencilSimple, Plus, TextT, Trash, UploadSimple, X } from "@phosphor-icons/react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminTimelinePictures() {
  const { admin } = useAdminAuth();
  const canDelete = admin?.allow_delete !== false;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({ title: "", file: null });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  const fetchItems = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/timeline-pictures`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => {
    if (preview) URL.revokeObjectURL(preview);
    setForm({ title: "", file: null });
    setPreview(null);
    setError("");
    setEditingItem(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const startEdit = (item) => {
    if (preview) URL.revokeObjectURL(preview);
    setEditingItem(item);
    setForm({ title: item.title || "", file: null });
    setPreview(`${BACKEND_URL}${item.image_url}`);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
    setDrawerOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (preview) URL.revokeObjectURL(preview);
    setForm((current) => ({ ...current, file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem && !form.file) { setError("Please upload an image."); return; }
    setError("");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      if (form.file) fd.append("file", form.file);
      const res = await fetch(
        editingItem ? `${BACKEND_URL}/api/timeline-pictures/${editingItem.id}` : `${BACKEND_URL}/api/timeline-pictures`,
        { method: editingItem ? "PUT" : "POST", body: fd }
      );
      if (!res.ok) throw new Error("Upload failed");
      await fetchItems();
      resetForm();
      setDrawerOpen(false);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    try {
      await fetch(`${BACKEND_URL}/api/timeline-pictures/${id}`, { method: "DELETE" });
      setItems((current) => current.filter((item) => item.id !== id));
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>Timeline Pictures</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {items.length} timeline picture{items.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setDrawerOpen(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
        >
          <Plus size={16} weight="bold" />
          Add Timeline Picture
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
            <Images size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>No timeline pictures yet</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>Add images for the timeline section.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} className="relative rounded-xl overflow-hidden border group" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
              <div className="relative aspect-[4/3]">
                <img src={`${BACKEND_URL}${item.image_url}`} alt={item.title || "Timeline picture"} className="w-full h-full object-cover" />
                {canDelete && (
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deleteId === item.id}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: "rgba(184,92,56,0.92)", color: "#fff" }}
                  >
                    <Trash size={14} weight="duotone" />
                  </button>
                )}
                <button
                  onClick={() => startEdit(item)}
                  className="absolute top-2 left-2 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: "rgba(26,92,55,0.92)", color: "#fff" }}
                >
                  <PencilSimple size={14} weight="duotone" />
                </button>
              </div>
              <div className="px-3 py-2" style={{ backgroundColor: "#fff" }}>
                <p className="text-xs font-medium truncate" style={{ color: "#1C211F" }}>{item.title || "Untitled timeline picture"}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-40" style={{ backgroundColor: "rgba(0,0,0,0.35)" }} onClick={() => setDrawerOpen(false)} />
      )}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${drawerOpen ? "translate-x-0" : "translate-x-full"}`}
        style={{ width: "min(420px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>{editingItem ? "Edit Timeline Picture" : "Add Timeline Picture"}</h3>
          <button onClick={() => setDrawerOpen(false)} className="p-1.5 rounded-lg" style={{ color: "#4A5450" }}>
            <X size={20} weight="bold" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Upload Image</label>
            <div
              className="relative flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer"
              style={{ borderColor: preview ? "#1A5C37" : "rgba(26,92,55,0.25)", backgroundColor: "#F7F5F0", minHeight: 160 }}
              onClick={() => fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              {preview ? (
                <img src={preview} alt="preview" className="max-h-36 rounded-lg object-cover" />
              ) : (
                <>
                  <UploadSimple size={28} weight="duotone" style={{ color: "#1A5C37" }} />
                  <p className="text-sm" style={{ color: "#4A5450" }}>{editingItem ? "Click to replace timeline image" : "Click to upload timeline image"}</p>
                </>
              )}
              {preview && (
                <button
                  type="button"
                  className="absolute top-2 right-2 p-1 rounded-full"
                  style={{ backgroundColor: "rgba(184,92,56,0.1)", color: "#B85C38" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
                    setPreview(editingItem ? `${BACKEND_URL}${editingItem.image_url}` : null);
                    setForm((current) => ({ ...current, file: null }));
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>

          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Title Optional</label>
            <div className="relative">
              <TextT size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#7A8A84" }} />
              <input
                type="text"
                placeholder="Timeline image title"
                value={form.title}
                onChange={(e) => setForm((current) => ({ ...current, title: e.target.value }))}
                className={inputCls}
                style={{ ...inputStyle, paddingLeft: "2.25rem" }}
              />
            </div>
          </div>

          {error && <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>{error}</p>}
        </form>
        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button type="button" onClick={() => setDrawerOpen(false)} className="flex-1 py-2.5 rounded-lg text-sm font-medium border" style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} className="flex-1 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0" }}>
            {submitting ? "Saving..." : editingItem ? "Save Image" : "Upload Image"}
          </button>
        </div>
      </div>
    </div>
  );
}
